<template>
  <div class="p-4 p-md-0 mb-4">
    <div class="row mt-3 mb-4">
      <div class="col col-sm col-12">
        <em class="fas fa-calendar-plus text-orange mr-1" />
        <span class="color-gray-7 font-16 font-weight-bold">
          {{ title }}
        </span>
      </div>
    </div>
    <validation-observer ref="requestForm" novalidate tag="form">
      <div class="row">
        <div class="col-md-3 col-sm-12 mt-3 mt-md-0 mb-2">
          <div class="color-gray-6 font-14 mb-1">
            Fecha de Solicitud:
          </div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-date-picker
              v-model="model.date"
              class-container="w-100"
              custom-error-msg="Fecha de Solicitud es requerida."
              :disabled="readOnly"
              :error="errors[0]"
              format="dd/MMM/yyyy"
              :min-date="currentDate"
              placeholder="DD/MM/YYYY"
            />
          </validation-provider>
        </div>
        <div class="col-md-1" />
        <div v-if="readOnly" class="col-md-3 col-sm-12 mt-3 mt-md-0 mb-2">
          <div class="color-gray-6 font-14 mb-2">
            Número de Solicitud:
          </div>
          <span class="mt-3">{{ model.requestNumber }}</span>
        </div>
        <div class="col-md-12 col-sm-12 mt-3 mt-md-0">
          <div class="color-gray-6 font-14 mb-1">Observación:</div>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-text-area
              v-model="model.comments"
              :count-classes="['float-right']"
              custom-error-msg="Favor escribir una observación."
              :disabled="readOnly"
              :error="errors[0]"
              :maxlength="3000"
              show-char-count
            />
          </validation-provider>
        </div>
        <div class="col-md-12 col-sm-12 mt-3 mt-md-0">
          <section-title
            container-class="m-0 mr-3 mt-3"
            content="Documentos a Añadir"
            :has-line="true"
          />
        </div>
        <div class="col-md-12 col-sm-12 mt-3 mt-md-0">
          <validation-provider
            v-for="(item, idx) in model.files"
            :key="idx"
            v-slot="{ errors }"
            :name="`fileUpload-${idx}`"
            :rules="`${validateRequiredDocument(item) ? 'required' : ''}`"
          >
            <custom-callout
              id="customCalloutUploadDownloadDocumentsPdf"
              :border-color-hex="`${errors[0] ? '#ff5b5c' : '#E2E2E2'}`"
              class="ml-2 mt-3"
              :has-toggle="false"
            >
              <div class="ml-md-3 mr-md-3 mt-md-1 mb-md-1">
                <div class="row d-flex align-items-center">
                  <div class="col-md-5 col-sm-12">
                    <span class="data-title d-block border-mobile">
                      Tipo de Documento:
                    </span>
                    <span class="font-16 color-black">
                      <span>{{ item.documentTypeName }}</span>
                    </span>
                  </div>
                  <div
                    v-if="item.content == null && item.documentId == null"
                    class="col-md-1 col-sm-12"
                  />
                  <div
                    v-if="
                      item.content == null &&
                        item.documentId == null &&
                        !readOnly
                    "
                    class="col-md-6 col-sm-12 justify-content-end text-right"
                  >
                    <action-button
                      id="actionBtnUploadDocumentsPdf"
                      i="fas fa-file-medical"
                      :variant="`${errors[0] ? 'danger' : 'success'} pr-1`"
                      @click="addDoc(idx)"
                    />
                  </div>
                  <div class="col-md-6 col-sm-12 fileDocument">
                    <custom-file-input
                      :id="`fileUpload-${item.documentTypeId}`"
                      :ref="`documentInputRef-${idx}`"
                      accept=".pdf"
                      :add-text="''"
                      :attachment-id="item.documentId"
                      class="file-input__extended"
                      custom-error-msg="Documento es requerido para esta solicitud."
                      :disabled="readOnly"
                      display-style="normal"
                      label="Archivo:"
                      label-position="left"
                      :name="item.filename"
                      :on-download="() => downloadDocument(item.documentId)"
                      :style="{
                        display:
                          item.content == null && item.documentId == null
                            ? 'none'
                            : '',
                      }"
                      :value="item.content"
                      @after-remove="onRemoveHandler(item)"
                      @input="(file) => onUploadFileHandler(file, item)"
                    />
                  </div>
                </div>
              </div>
            </custom-callout>
            <custom-error
              v-if="errors[0]"
              message="Documento es requerido para esta solicitud"
              style="font-size:0.785rem"
              variant="ml-2 mb-2 mt-0 no-border"
            />
          </validation-provider>
        </div>
      </div>
    </validation-observer>
    <div class="mt-3 mb-3 align-items-center d-flex justify-content-end">
      <action-button
        id="btnCancel"
        i="fas fa-times-circle"
        :text="`${readOnly ? 'Cerrar' : 'Cancelar'}`"
        variant="outline-danger mr-3 pl-3 pr-4"
        @click="$emit('cancel')"
      />
      <action-button
        id="btnSave"
        :disabled="readOnly"
        i="fas fa-save"
        text="Someter"
        variant="success px-3"
        @click="onSaveHandler"
      />
    </div>
  </div>
</template>

<script>
import objectToFormData from 'object-to-formdata';
import moment from 'moment';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

//component libs
import ActionButton from 'ActionButton';
import CustomDatePicker from 'custom-date-picker';
import CustomTextArea from 'CustomTextArea';
import SectionTitle from 'SectionTitle';
import CustomFileInput from 'CustomFileInput';
import CustomCallout from 'custom-callout';
import CustomError from 'custom-error';
//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
// import { RequestType } from '@/utils/constants/compensatoryRequestType.js';

//services
import {
  getDocuments as _getDocuments,
  saveRequest as _saveRequest,
} from '@/services/provider/extended-academic-year.api.js';
import { downloadDocumentByAttachmentId as _downloadDocumentByAttachmentId } from '@/services/common/document.api.js';

export default {
  name: 'RequestAEE',
  mixins: [alertMixin],
  components: {
    ValidationObserver,
    ValidationProvider,
    ActionButton,
    CustomDatePicker,
    CustomTextArea,
    SectionTitle,
    CustomFileInput,
    CustomCallout,
    CustomError,
  },
  props: {
    title: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: () => {},
    },
    academicYearId: {
      type: Number,
      default: null,
    },
    contractId: {
      type: Number,
      default: null,
    },
    requestTypeId: {
      type: Number,
      default: null,
    },
    request: {
      type: Object,
      default: () => {},
    },
    requestAssignStartDateValidations: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data: () => ({
    model: {
      requestId: 0,
      date: null,
      reason: null,
      files: null,
      requestNumber: null,
      comments: null,
    },
    readOnly: false,
  }),
  mounted() {
    //Get Document Extend Academic year
    // if (this.requestTypeId == RequestType.Regression){

    // }
    if (this.request.requestId != 0) {
      this.model.requestId = this.request.requestId;
      this.model.date = this.request.requestDate;
      this.model.comments = this.request.comments;
    }

    _getDocuments(this.academicYearId, this.contractId).then((response) => {
      if (response.data !== undefined) {
        this.model.files = response.data;
      }
    });
  },
  computed: {
    maxDate() {
      return this.requestAssignStartDateValidations?.maxDate;
    },
    getMinimunDateInclusive() {
      return moment(this.requestAssignStartDateValidations.minDate)
        .subtract(1, 'days')
        .format('YYYY-MM-DD HH:mm:ss');
    },
    currentDate() {
      let date = new Date();
      date.setDate(date.getDate() - 1);
      return date;
    },
  },
  methods: {
    async onSaveHandler() {
      const isFormValid = await this.$refs.requestForm.validate();

      if (!isFormValid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }

      const payload = {
        requestId: this.model.requestId,
        contractId: this.contractId,
        requestDate: moment(this.model.date)
          .locale('es-PR')
          .format('YYYY-MM-DD'),
        files: this.model.files.map((f) => ({
          documentId: f.documentId || null,
          filename: f.filename,
          documentTypeId: f.documentTypeId,
          documentTypeName: f.documentTypeName,
        })),
        comment: this.model.comments,
      };

      const params = objectToFormData(payload, { indices: true });

      this.model.files.forEach((f) => {
        if (f.content) {
          params.append('files', f.content);
        }
      });

      const response = await _saveRequest(params).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
      });

      if (response?.data) {
        const result = response.data;
        this.ShowToast(
          'Proceso Exitoso',
          'La solicitud de Año Escolar Extendido ha sido sometida exitosamente.',
          'success'
        );
        this.readOnly = true;
        this.model.requestNumber = result;
        this.$emit('saved', result);
      }
    },
    async onRemoveHandler(item) {
      item.filename = '';
    },
    addDoc(idx) {
      this.$refs[`documentInputRef-${idx}`][0].$refs.fileuploadRef.click();
    },
    onUploadFileHandler(file, item) {
      item.content = file;
      item.filename = file.name;
    },
    downloadDocument(documentId) {
      _downloadDocumentByAttachmentId(documentId).catch((err) => {
        this.ShowToast('Alerta', err.response.data.message || 'error', 'error');
      });
    },
    validateRequiredDocument(item) {
      if (this.request.requestId !== 0) {
        return item.isRequired && !item.filename;
      }
      return item.isRequired;
    },
  },
};
</script>
<style lang="scss">
.file-input__extended .font-weight-normal {
  width: 54rem;
}
</style>
